import { prop, assocPath } from 'ramda';
import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { theme, createStandaloneToast } from '@frontend/components';

import * as opDefActions from 'modules/opDefs/data/actions';
import { fetchOpDefs } from 'modules/opDefs/data/sagas';

import addOnsService from 'services/addOnsService';
import discountsService from 'services/discountsService';
import profitItemsService from 'services/profitItemsService';

import { initMaterialStore } from 'store/materials/sagas';
import { getGroupId } from 'store/session/selectors';

import history from 'utils/browserHistory';
import { defer } from 'utils/helpers';
import { getErrorMessage } from 'utils/stringUtils';

import * as actions from './actions';
import * as selectors from './reducer';
import service from './service';

const { toast } = createStandaloneToast({ theme });
const types = actions.types;

export default function* root() {
  yield takeLatest(types.UPDATE_PROCESS_REQUEST, watchUpdateProcess);
  yield takeLatest(types.DELETE_PROCESS_REQUEST, watchDeleteProcess);
  yield takeLatest(types.CREATE_MATERIAL_REQUEST, watchCreateMaterial);
  yield takeLatest(types.UPDATE_MATERIAL_REQUEST, watchUpdateMaterial);
  yield takeLatest(types.DELETE_MATERIAL_REQUEST, watchDeleteMaterial);
  yield takeLatest(types.CREATE_FINISH_REQUEST, watchCreateFinish);
  yield takeLatest(types.UPDATE_FINISH_REQUEST, watchUpdateFinish);
  yield takeLatest(types.DELETE_FINISH_REQUEST, watchDeleteFinish);
  yield takeLatest(
    types.ADD_OPERATIONS_OPEN_MODAL,
    watchAddOperationsOpenModal,
  );
  yield takeLatest(types.ADD_ADD_ONS_REQUEST, watchAddAddOns);
  yield takeLatest(types.ADD_PROFIT_ITEMS_REQUEST, watchAddProfitItems);
  yield takeLatest(types.ADD_DISCOUNTS_REQUEST, watchAddDiscounts);
  yield takeLatest(types.ADD_OPERATIONS_REQUEST, watchAddOperations);
  yield takeLatest(types.ADD_MATERIALS_REQUEST, watchAddMaterials);
  yield takeLatest(types.FORMULA_UPDATE_REQUEST, watchUpdateFormula);
  yield takeLatest(types.FORMULA_COMPILE_REQUEST, watchCompileFormula);
  yield takeLatest(types.SEARCH_ADD_ONS_REQUEST, watchSearchAddOns);
  yield takeLatest(types.SEARCH_PROFIT_ITEMS_REQUEST, watchSearchProfitItems);
  yield takeLatest(types.SEARCH_DISCOUNTS_REQUEST, watchSearchDiscounts);
}

export function* initProcessDetailsPage({ match }) {
  const processId = match.params.processId;
  const process = yield call(service.getProcess, processId);
  yield put(
    opDefActions.addOpDefsToStore({ opDefs: process.operationDefinitions }),
  );
  const opDefIds = yield call(fetchOpDefs);
  yield put(opDefActions.receiveOpDefs(opDefIds));
  yield call(initMaterialStore);

  // fetch secondary resources needed for the page
  yield fork(handleFetchAddOns);
  yield fork(handleFetchDiscounts);
  yield fork(handleFetchProfitItems);

  yield put(actions.init(process));
}

function* handleFetchAddOns() {
  try {
    const groupId = yield select(getGroupId);
    const addOns = yield call(addOnsService.list, groupId);
    yield put(actions.loadAddOns.success({ addOns }));
  } catch (error) {
    yield put(actions.loadAddOns.error(error));
  }
}

function* handleFetchProfitItems() {
  try {
    const groupId = yield select(getGroupId);
    const profitItems = yield call(profitItemsService.list, groupId);
    yield put(actions.loadProfitItems.success({ profitItems }));
  } catch (error) {
    yield put(actions.loadProfitItems.error(error));
  }
}

function* handleFetchDiscounts() {
  try {
    const groupId = yield select(getGroupId);
    const discounts = yield call(discountsService.list, groupId);
    yield put(actions.loadDiscounts.success({ discounts }));
  } catch (error) {
    yield put(actions.loadDiscounts.error(error));
  }
}

export function* watchUpdateProcess(action) {
  try {
    const { processId, data } = action.payload;
    let payload = data;
    if ('operationDefinitions' in data) {
      const process = yield select(selectors.getProcess);
      const opFactory = { ...process.opFactory };
      opFactory.opDefTemplates = data.operationDefinitions.map(prop('id'));
      payload = { ...payload, opFactory };
    }
    const response = yield call(service.updateProcess, processId, payload);
    yield put(actions.updateProcess.success(response));
  } catch (error) {
    yield put(actions.updateProcess.error(error));
    yield call(toast, {
      title: 'Error',
      status: 'error',
      description: getErrorMessage(error),
    });
  }
}

function* watchSearchAddOns(action) {
  try {
    const groupId = yield select(getGroupId);
    const addOns = yield call(addOnsService.list, groupId, {
      search: action.payload.searchTerm,
    });
    yield put(actions.searchAddOns.success({ addOns }));
  } catch (error) {
    yield put(actions.searchAddOns.error(error));
  }
}

function* watchSearchProfitItems(action) {
  try {
    const groupId = yield select(getGroupId);
    const profitItems = yield call(profitItemsService.list, groupId, {
      search: action.payload.searchTerm,
    });
    yield put(actions.searchProfitItems.success({ profitItems }));
  } catch (error) {
    yield put(actions.searchProfitItems.error(error));
  }
}

function* watchSearchDiscounts(action) {
  try {
    const groupId = yield select(getGroupId);
    const discounts = yield call(discountsService.list, groupId, {
      search: action.payload.searchTerm,
    });
    yield put(actions.searchDiscounts.success({ discounts }));
  } catch (error) {
    yield put(actions.searchDiscounts.error(error));
  }
}

export function* watchCreateMaterial(action) {
  let e = null;
  try {
    const { processId, materialIds } = action.payload;
    const response = yield call(service.createMaterial, processId, materialIds);
    yield put(actions.createMaterial.success(processId, response));
  } catch (error) {
    yield put(actions.createMaterial.error(error));
    yield call(toast, {
      title: 'Error',
      status: 'error',
      description: getErrorMessage(error),
    });
    e = error;
  }
  action.payload.cb && action.payload.cb(e);
}

export function* watchUpdateMaterial(action) {
  try {
    const { materialId, data } = action.payload;
    const response = yield call(service.updateMaterial, materialId, data);
    yield put(actions.updateMaterial.success(response));
  } catch (error) {
    yield put(actions.updateMaterial.error(error));
    yield call(toast, {
      title: 'Error',
      status: 'error',
      description: getErrorMessage(error),
    });
  }
}

export function* watchDeleteMaterial(action) {
  try {
    const { processId, materialId } = action.payload;
    yield call(service.deleteMaterial, materialId);
    yield put(actions.deleteMaterial.success(processId, materialId));
  } catch (error) {
    yield put(actions.deleteMaterial.error(error));
    yield call(toast, {
      title: 'Error',
      status: 'error',
      description: getErrorMessage(error),
    });
  }
}

export function* watchCreateFinish(action) {
  let e = null;
  try {
    const { processId, finishIds } = action.payload;
    const response = yield call(service.createFinish, processId, finishIds);
    yield put(actions.createFinish.success(processId, response));
  } catch (error) {
    yield put(actions.createFinish.error(error));
    yield call(toast, {
      title: 'Error',
      description: getErrorMessage(error),
      status: 'error',
    });
    e = error;
  }
  action.payload.cb && action.payload.cb(e);
}

export function* watchUpdateFinish(action) {
  try {
    const { finishId, data } = action.payload;
    const response = yield call(service.updateFinish, finishId, data);
    yield put(actions.updateFinish.success(response));
  } catch (error) {
    yield put(actions.updateFinish.error(error));
    yield call(toast, {
      title: 'Error',
      description: getErrorMessage(error),
      status: 'error',
    });
  }
}

export function* watchDeleteFinish(action) {
  try {
    const { processId, finishId } = action.payload;
    yield call(service.deleteFinish, finishId);
    yield put(actions.deleteFinish.success(processId, finishId));
  } catch (error) {
    yield put(actions.deleteFinish.error(error));
    yield call(toast, {
      title: 'Error',
      description: getErrorMessage(error),
      status: 'error',
    });
  }
}

function* watchAddOperationsOpenModal() {
  yield put(opDefActions.searchOpDefs.request({ query: '' }));
}

function* watchAddOperations(action) {
  try {
    const { type, opDefDescriptors } = action.payload;
    const process = yield select(selectors.getProcess);
    let response;
    if (type === 'operations') {
      response = yield call(service.updateProcess, process.id, {
        opFactory: assocPath(
          ['opDefTemplates'],
          opDefDescriptors,
          process.opFactory,
        ),
      });
    } else if (type === 'finishes') {
      response = yield call(service.updateProcess, process.id, {
        finishOpDefIds: opDefDescriptors.map(prop('opDefId')),
      });
    }
    yield put(actions.addOperations.success(response));
    yield put(actions.addOperations.closeModal());
  } catch (error) {
    yield put(actions.addOperations.error());
    yield call(toast, {
      title: 'Error',
      description: getErrorMessage(error),
      status: 'error',
    });
  }
}

function* watchAddAddOns(action) {
  try {
    const { addOnDefTemplate } = action.payload;
    const process = yield select(selectors.getProcess);
    const response = yield call(service.updateProcess, process.id, {
      addOnFactory: assocPath(
        ['addOnDefTemplates'],
        addOnDefTemplate,
        process.addOnFactory,
      ),
    });
    yield put(actions.addAddOns.success({ process: response }));
  } catch (error) {
    yield put(actions.addAddOns.error(error));
  }
}

function* watchAddProfitItems(action) {
  try {
    const { profitItemDefTemplate } = action.payload;
    const process = yield select(selectors.getProcess);
    const response = yield call(service.updateProcess, process.id, {
      profitItemFactory: assocPath(
        ['profitItemDefTemplates'],
        profitItemDefTemplate,
        process.profitItemFactory,
      ),
    });
    yield put(actions.addProfitItems.success({ process: response }));
  } catch (error) {
    yield put(actions.addProfitItems.error(error));
  }
}

function* watchAddDiscounts(action) {
  try {
    const { discountDefTemplate } = action.payload;
    const process = yield select(selectors.getProcess);
    const response = yield call(service.updateProcess, process.id, {
      discountFactory: assocPath(
        ['discountDefTemplates'],
        discountDefTemplate,
        process.discountFactory,
      ),
    });
    yield put(actions.addDiscounts.success({ process: response }));
  } catch (error) {
    yield put(actions.addDiscounts.error(error));
  }
}

function* watchAddMaterials(action) {
  try {
    const { processId, supplierMaterialIds } = action.payload;
    const newProcessMaterials = yield call(
      service.updateProcessMaterials,
      processId,
      { supplierMaterialIds },
    );
    const oldProcess = yield select(selectors.getProcess, processId);
    const nextProcess = {
      ...oldProcess,
      processMaterials: newProcessMaterials,
    };
    yield put(actions.addMaterials.success(nextProcess));
    yield put(actions.addMaterials.closeModal());
  } catch (error) {
    yield put(actions.addMaterials.error());
    yield call(toast, {
      title: 'Error',
      description: getErrorMessage(error),
      status: 'error',
    });
  }
}

function* watchDeleteProcess() {
  const deferred = defer();
  try {
    const processId = yield select(selectors.getProcessId);
    yield call(service.deleteProcess, processId);
    history.push('/processes');
    yield call(toast, {
      title: 'Success',
      status: 'success',
      description: 'Process was successfully deleted.',
    });
  } catch (error) {
    yield put(actions.deleteProcess.error(error));
    yield call(toast, {
      title: 'Error',
      description: getErrorMessage(error),
      status: 'error',
    });
  } finally {
    deferred.resolve();
  }
}

function* watchCompileFormula(action) {
  const { payload } = action;
  const { formula } = payload;
  const processId = yield select(selectors.getProcessId);
  const supplierId = yield select(getGroupId);

  try {
    yield call(service.compileFormula, {
      processId,
      formula,
      supplierId,
    });
    yield put(actions.formulaEdit.compileSuccess());
  } catch (error) {
    yield put(
      actions.formulaEdit.compileError({ error: getErrorMessage(error) }),
    );
  }
}

function* watchUpdateFormula(action) {
  const { payload } = action;
  const { formula, revertedFrom } = payload;
  const processId = yield select(selectors.getProcessId);

  try {
    const response = yield call(service.updateProcessFormula, {
      processId,
      formula,
      revertedFrom,
    });
    yield put(
      actions.formulaEdit.updateSuccess({ formula: response, processId }),
    );
  } catch (error) {
    yield put(
      actions.formulaEdit.updateError({ error: getErrorMessage(error) }),
    );
  }
}
